import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import { makeStyles } from "@material-ui/core/styles";
import Textarea from "@mui/joy/Textarea";
import TableSortLabel from "@mui/material/TableSortLabel";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { TextField } from "@material-ui/core";
import TablePagination from "@mui/material/TablePagination";
import {
  formatColumnValue,
  formatColumnDate,
  formatColumnNumber,
} from "../../../../hooks/Formatter";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";

const useStyles = makeStyles({
  tableContainer: {
    maxHeight: 400, // Set the height for the table container to enable scrolling
  },
  table: {
    minWidth: 800,
    borderCollapse: "separate !important",
    borderSpacing: "0 16px !important",
    border: "none !important",
  },
  tableHead: {
    boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.06) !important",
    borderSpacing: "0 !important",
    borderRadius: "4px !important",
    border: "1px solid #E7EFFE !important",
    position: "sticky", // Make the header sticky
    top: 0,
    backgroundColor: "#FFF", // Ensure the background color matches your design
    zIndex: 1, // Ensure it stays on top of the other elements
  },
  tableRow: {
    boxShadow:
      "0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 2px 16px 0px rgba(0, 0, 0, 0.10) !important",
  },
  tableCell: {
    padding: "5px 20px !important",
    fontSize: "12px !important",
    fontFamily: "Poppins, sans-serif",
    fontWeight: "500 !important",
  },
  sortLabel: {
    color: "#929497 !important",
    fontSize: "14px !important",
    fontFamily: "Poppins, sans-serif",
  },
  alertTypeCell: {
    borderRadius: "4px !important",
    padding: "0px 10px !important",
    textAlign: "center !important",
    width: "fit-content !important",
  },
  feedbackTitle: {
    fontFamily: "Poppins, sans-serif",
    fontSize: "12px !important",
    color: "#333 !important",
    fontWeight: "500 !important",
    margin: "10px 0px!important",
  },
  feedbackTextarea: {
    borderRadius: "4px !important",
    position: "relative",
    width: "100%",
    border: "1px solid #C6C7C8 !important",
    background: "#FFF !important",
    fontSize: "12px !important",
  },
  feedbackSuccessIcon: {
    color: "#008000a8 !important",
    fontSize: "50px !important",
  },
  feedbackSuccessText: {
    fontSize: "13px !important",
    marginTop: "5px",
    marginBottom: "5px",
  },
  wordCount: {
    position: "absolute",
    right: 50,
    fontFamily: "Poppins, sans-serif",
    fontSize: "12px !important",
    color: "#333 !important",
    fontWeight: "400 !important",
  },
});

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const headCells = [
  { id: "Priority", numeric: true, disablePadding: false, label: "Priority" },
  { id: "Entity", numeric: false, disablePadding: false, label: "Entity" },
  { id: "Name", numeric: true, disablePadding: false, label: "Entity Name" },
  { id: "Metric", numeric: false, disablePadding: false, label: "Alert Type" },
  {
    id: "Metric_value",
    numeric: true,
    disablePadding: false,
    label: "Alert Value",
  },
  { id: "Genrated_date", numeric: true, disablePadding: false, label: "Date" },
  { id: "comment", numeric: false, disablePadding: false, label: "" },
];

const headCellsForIF = [
  { id: "Function", numeric: false, disablePadding: false, label: "Function" },
  { id: "Network", numeric: false, disablePadding: false, label: "Network" },
  {
    id: "Insight_Type",
    numeric: false,
    disablePadding: false,
    label: "Insight Type",
  },
  {
    id: "Impact_Value",
    numeric: true,
    disablePadding: false,
    label: "Impact Value",
  },
  { id: "Week", numeric: false, disablePadding: false, label: "Week" },
];

const EnhancedTableHead = React.memo(({ sortBy, sortOrder, onRequestSort }) => {
  const classes = useStyles();

  const createSortHandler = useCallback(
    (property) => () => {
      onRequestSort(property);
    },
    [onRequestSort],
  );

  return (
    <TableHead className={classes.tableHead}>
      <TableRow>
        {window.location.pathname !== "/if" && (
          <TableCell className={classes.tableCell}>Reviewed</TableCell>
        )}
        {(window.location.pathname === "/if" ? headCellsForIF : headCells)?.map(
          (headCell) => (
            <TableCell key={headCell.id} className={classes.sortLabel}>
              <TableSortLabel
                hideSortIcon={true}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {headCell.id !== "AlertValue" &&
                  headCell.id !== "comment" &&
                  (sortBy === headCell.id && sortOrder === "desc" ? (
                    <ArrowDropDownIcon />
                  ) : (
                    <ArrowDropUpIcon />
                  ))}
              </TableSortLabel>
            </TableCell>
          ),
        )}
      </TableRow>
    </TableHead>
  );
});

const ReviewTable = React.memo(
  ({
    viewAllAlertData,
    setFeedBackComment,
    alertsRows,
    handleShowFeedback,
    handleCheckboxClick,
    selected,
    showFeedback,
    showFeedbackSuccess,
    feedBackComment,
    handleFeedBackChange,
    handleComment,
    _commentFlag,
    _reviewComment,
    handleViewPowerBi,
  }) => {
    const defaultSortOrder =
      alertsRows[0]?.Module === "SNO" || alertsRows[0]?.Module === "DOM"
        ? "desc"
        : "asc";
    const defaultSortBy =
      alertsRows[0]?.Module === "SNO" || alertsRows[0]?.Module === "DOM"
        ? "Metric_value"
        : "Priority";

    const classes = useStyles();
    const [sortBy, setSortBy] = useState(defaultSortBy);
    const [sortOrder, setSortOrder] = useState(defaultSortOrder);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    // const [searchTerm, setSearchTerm] = useState("");
    const searchTerm = "";
    const isSelected = useCallback(
      (id) => selected.indexOf(id) !== -1,
      [selected],
    );

    const handleRequestSort = useCallback(
      (property) => {
        const isAsc = sortBy === property && sortOrder === "asc";
        setSortOrder(isAsc ? "desc" : "asc");
        setSortBy(property);
      },
      [sortBy, sortOrder],
    );

    const handleChangePage = useCallback((_event, newPage) => {
      setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    }, []);

    const filteredData = useMemo(() => {
      return alertsRows.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );
    }, [alertsRows, searchTerm]);

    const sortedData = useMemo(
      () => stableSort(filteredData, getComparator(sortOrder, sortBy)),
      [filteredData, sortOrder, sortBy],
    );

    const paginatedData = useMemo(() => {
      return sortedData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      );
    }, [sortedData, page, rowsPerPage]);

    const wordCount = useMemo(
      () => feedBackComment.length,
      [feedBackComment, viewAllAlertData],
    );
    const maxWords = 100;

    const handleClick = useCallback(
      (event, id, comment) => {
        const newSelected = event.target.checked
          ? [...selected, id]
          : handleClickComment(id, comment);
        handleCheckboxClick(newSelected, event.target.checked); // Update state based on selection
        handleShowFeedback(event.target.checked); // Show feedback form if needed
        handleComment(id, comment);
        if (event.target.checked) {
          setFeedBackComment("");
        }
      },
      [selected, handleCheckboxClick, handleShowFeedback],
    );

    const handleCommentChange = useCallback(
      (event) => {
        handleFeedBackChange(event.target.value);
      },
      [handleFeedBackChange],
    );

    const handleClickComment = (id, comment) => {
      setFeedBackComment(comment);
      handleComment(id, comment);
    };

    const getValue = (row) => {
      console.log("row>>>>>>>>>>>>>>", row);
      if (
        ["Incident rate increase WoW", "Incident rate increase MoM"].includes(
          row?.Metric,
        )
      ) {
        return formatColumnNumber(row.Metric_value, "Percentage");
      } else if (["Pending action > 2 days"].includes(row?.Metric)) {
        return formatColumnNumber(row.Metric_value, "Count");
      } else if (["Capacity Breach"].includes(row?.Metric)) {
        return formatColumnNumber(row.Metric_value, "weeks");
      } else if (["ATP To confirm Quantity"].includes(row?.Metric)) {
        return formatColumnNumber(row.Metric_value, "Count");
      } else if (["Confirm qty adjustment"].includes(row?.Metric)) {
        return formatColumnNumber(row.Metric_value, "Count");
      } else if (["Low Payload or COF"].includes(row?.Metric)) {
        return formatColumnNumber(row.Metric_value, "Count");
      } else if (row?.Module === "ERM") {
        return formatColumnNumber(row.Metric_value, "Percentage");
      } else if (row?.Module === "DOM" && row?.Metric === "COF_Increase") {
        return formatColumnNumber(row.Metric_value, "Percentage");
      } else if (row?.Module === "DRP") {
        if (row.Metric === "Waste Risk") {
          return formatColumnNumber(row.Metric_value, "Dollars");
        } else {
          return formatColumnNumber(row.Metric_value, "Percentage");
        }
      }
      return row?.Module === "RR" ||
        row?.Module === "CP" ||
        row?.ScenarioName ||
        row?.Temperature
        ? row?.Temperature
          ? formatColumnNumber(row.Metric_value, "Count")
          : formatColumnNumber(row.Metric_value, "Percentage")
        : formatColumnNumber(
            row.Metric_value,
            row.units ? row.units : row.Units,
          );
    };
    return (
      <Box sx={{ width: "100%" }}>
        <TableContainer className={classes.tableContainer}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="medium"
            stickyHeader // Ensure the table header sticks during scrolling
          >
            <EnhancedTableHead
              sortBy={sortBy}
              sortOrder={sortOrder}
              onRequestSort={handleRequestSort}
            />

            {window.location.pathname === "/if" ? (
              <TableBody className={classes.tableBody}>
                {paginatedData?.map((row, _index) => {
                  const isItemSelected = isSelected(row.Alert_id);
                  const Review_Flag = row.Review_Flag === 1;
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.Alert_id}
                      className={classes.tableRow}
                      selected={isItemSelected || Review_Flag}
                      style={{ cursor: "pointer", fontSize: "10px" }}
                    >
                      {/* <TableCell padding="checkbox" className={classes.tableCell}>
                      <Checkbox
                        color="primary"
                        checked={isItemSelected || Review_Flag}
                        onChange={(event) => {
                          handleClick(event, row.Alert_id, row.Comment);
                        }} // Use onChange instead of onClick for checkboxes
                        sx={{
                          color: "#59595A",
                          "&.Mui-checked": {
                            color: "#59595A",
                          },
                        }}
                      />
                    </TableCell> */}
                      {/* <TableCell
                      className={classes.tableCell}
                      onClick={() => {
                        handleViewPowerBi(row);
                      }}
                    >
                      {index + 1}
                    </TableCell> */}
                      <TableCell
                        className={classes.tableCell}
                        onClick={() => handleViewPowerBi(row)}
                      >
                        {row?.Function}
                      </TableCell>
                      <TableCell
                        className={classes.tableCell}
                        onClick={() => handleViewPowerBi(row)}
                      >
                        {row?.Network}
                      </TableCell>
                      <TableCell
                        className={classes.tableCell}
                        onClick={() => handleViewPowerBi(row)}
                      >
                        <div
                          className={classes.alertTypeCell}
                          style={{
                            backgroundColor:
                              row.Metric === "Under Capacity"
                                ? "#FDF8E6"
                                : "#FBE8E9",
                          }}
                        >
                          {formatColumnValue(row?.Insight_Type)}
                        </div>
                      </TableCell>
                      <TableCell
                        className={classes.tableCell}
                        onClick={() => handleViewPowerBi(row)}
                      >
                        {row?.Impact_Value}
                      </TableCell>
                      {/* <TableCell
                      className={classes.tableCell}
                      onClick={() => handleViewPowerBi(row)}
                    >
                      {getValue(row)}{" "}
                    </TableCell> */}
                      <TableCell
                        className={classes.tableCell}
                        onClick={() => handleViewPowerBi(row)}
                      >
                        {formatColumnDate(row?.Week)}
                      </TableCell>
                      {Review_Flag && (
                        <TableCell className={classes.tableCell}>
                          <ChatOutlinedIcon
                            onClick={() =>
                              handleClickComment(row.Alert_id, row.Comment)
                            }
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            ) : (
              <TableBody className={classes.tableBody}>
                {paginatedData?.map((row, index) => {
                  const isItemSelected = isSelected(row.Alert_id);
                  const Review_Flag = row.Review_Flag === 1;
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.Alert_id}
                      className={classes.tableRow}
                      selected={isItemSelected || Review_Flag}
                      style={{ cursor: "pointer", fontSize: "10px" }}
                    >
                      <TableCell
                        padding="checkbox"
                        className={classes.tableCell}
                      >
                        <Checkbox
                          color="primary"
                          checked={isItemSelected || Review_Flag}
                          onChange={(event) => {
                            handleClick(event, row.Alert_id, row.Comment);
                          }} // Use onChange instead of onClick for checkboxes
                          sx={{
                            color: "#59595A",
                            "&.Mui-checked": {
                              color: "#59595A",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        className={classes.tableCell}
                        onClick={() => {
                          handleViewPowerBi(row);
                        }}
                      >
                        {index + 1}
                      </TableCell>
                      <TableCell
                        className={classes.tableCell}
                        onClick={() => handleViewPowerBi(row)}
                      >
                        {row?.Entity}
                      </TableCell>
                      <TableCell
                        className={classes.tableCell}
                        onClick={() => handleViewPowerBi(row)}
                      >
                        {row?.Name}
                      </TableCell>
                      <TableCell
                        className={classes.tableCell}
                        onClick={() => handleViewPowerBi(row)}
                      >
                        <div
                          className={classes.alertTypeCell}
                          style={{
                            backgroundColor:
                              row.Metric === "Under Capacity"
                                ? "#FDF8E6"
                                : "#FBE8E9",
                          }}
                        >
                          {formatColumnValue(row?.Metric)}
                        </div>
                      </TableCell>
                      <TableCell
                        className={classes.tableCell}
                        onClick={() => handleViewPowerBi(row)}
                      >
                        {row?.Units === "Count"
                          ? Number(getValue(row))?.toFixed(0)
                          : getValue(row)}{" "}
                      </TableCell>
                      <TableCell
                        className={classes.tableCell}
                        onClick={() => handleViewPowerBi(row)}
                      >
                        {
                          // row?.Module === "CP" ||
                          //   row?.Module === "DRP" ||
                          row?.Module === "POOS" ||
                          window?.location?.pathname === "/ds"
                            ? formatColumnDate(row?.Date || "")
                            : formatColumnDate(row?.Genrated_date)
                        }
                      </TableCell>
                      {Review_Flag && (
                        <TableCell className={classes.tableCell}>
                          <ChatOutlinedIcon
                            onClick={() =>
                              handleClickComment(row?.Alert_id, row?.Comment)
                            }
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
          {showFeedback && (
            <>
              <Typography className={classes.feedbackTitle}>
                Feedback Comment
              </Typography>
              <TextField
                name="Outlined"
                minRows={7}
                placeholder="Type here"
                variant="outlined"
                multiline
                fullWidth
                value={feedBackComment}
                onChange={handleCommentChange}
                inputProps={{ maxLength: maxWords }}
              />
              <Typography className={classes.wordCount}>
                {wordCount}/{maxWords}
              </Typography>
            </>
          )}
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        {showFeedbackSuccess && (
          <Box className={classes.successMessage}>
            <CheckCircleIcon className={classes.successIcon} />
            <Typography className={classes.successText}>
              Your Feedback has been successfully submitted!
            </Typography>
          </Box>
        )}
      </Box>
    );
  },
);

ReviewTable.propTypes = {
  alertsRows: PropTypes.array.isRequired,
  handleShowFeedback: PropTypes.func.isRequired,
  handleCheckboxClick: PropTypes.func.isRequired,
  selected: PropTypes.array.isRequired,
  showFeedback: PropTypes.bool.isRequired,
  showFeedbackSuccess: PropTypes.bool.isRequired,
  feedBackComment: PropTypes.string.isRequired,
  handleFeedBackChange: PropTypes.func.isRequired,
  handleComment: PropTypes.func.isRequired,
  commentFlag: PropTypes.bool.isRequired,
  reviewComment: PropTypes.string.isRequired,
  handleViewPowerBi: PropTypes.func.isRequired,
};

export default ReviewTable;
